import { useLinkedIn } from "react-linkedin-login-oauth2";
import * as Mixpanel from "../../services/mixpanel";
import { getLinkedInScopesV3 } from "../../services/auth";

import useRequest from "../../api/useRequest";
import { LinkedInIntegrationsRequestFactory } from "../../api/requestFactory";
import { useNavigate } from "react-router-dom";
import { useContext } from "react";
import { NotificationContext } from "../../context/notification-context";
import { ERROR_CODES } from "../../api/errorCodes";

// Detect mobile devices where popup OAuth doesn't work reliably
function isMobileDevice() {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  );
}

// Generate LinkedIn OAuth URL for redirect flow
function getLinkedInOAuthUrl(clientId, redirectUri, scopes, state) {
  const params = new URLSearchParams({
    response_type: "code",
    client_id: clientId,
    redirect_uri: redirectUri,
    scope: scopes,
    state: state,
  });
  return `https://www.linkedin.com/oauth/v2/authorization?${params.toString()}`;
}

export default function LinkedInAuth(props) {
  const { requestMaker } = useRequest();
  const navigate = useNavigate();
  const { showNotification } = useContext(NotificationContext);

  const redirectUri = `${window.location.origin}/integrations/linkedin/callback`;

  const client_id = import.meta.env.VITE_LINKEDIN_CLIENT_ID_V3;
  const scopes = getLinkedInScopesV3();

  const { linkedInLogin } = useLinkedIn({
    clientId: client_id,
    scope: scopes,
    redirectUri: redirectUri,
    onSuccess: async (code) => {
      if (props?.callback) {
        props.callback(code, redirectUri, client_id);
      } else {
        requestMaker(
          LinkedInIntegrationsRequestFactory.createNewLinkedInIntegrationRequest(
            code,
            redirectUri,
            client_id
          )
        )
          .then(() => {
            Mixpanel.track("LinkedIn Integration Completed", {});
            if (props.onClose) {
              props.onClose({ code, redirectUri });
            } else {
              navigate(0);
            }
          })
          .catch((error) => {
            Mixpanel.track("LinkedIn Integration Failed", {
              error: error,
            });

            const code = error?.response?.data?.code;
            if (
              code === ERROR_CODES.LINKEDIN_MIGRATE_ERROR_ACCOUNT ||
              code === ERROR_CODES.LINKEDIN_MIGRATE_ERROR_WORKSPACE
            ) {
              props?.onClose(error?.response?.data);
            } else {
              let errorContent =
                error?.response?.data?.errors[0] ||
                "Something went wrong while connecting your LinkedIn account. Please try again later.";
              showNotification(
                {
                  title: "Error",
                  content: errorContent,
                },
                "error"
              );
            }
            console.error(error);
          });
      }
    },
    onError: (error) => {
      if (error.errorMessage === "User closed the popup") {
        return;
      }
      Mixpanel.track("LinkedIn Integration Failed", {
        error: error.errorMessage,
      });
      showNotification(
        {
          title: "Error",
          content:
            "Something went wrong while connecting your LinkedIn account. Please try again later.",
        },
        "error"
      );
      console.error(error);
    },
  });

  const handleClick = () => {
    if (isMobileDevice()) {
      // For mobile devices, use redirect flow instead of popup
      // Store return URL and callback info in localStorage
      const state = Math.random().toString(36).substring(2, 15);
      localStorage.setItem(
        "linkedin_oauth_state",
        JSON.stringify({
          state,
          returnUrl: window.location.href,
          hasCallback: !!props?.callback,
          // The redirect unmounts the public page, so the callback handler needs the link to connect it.
          publicLink: props?.publicLink,
        })
      );

      // Redirect to LinkedIn OAuth
      window.location.href = getLinkedInOAuthUrl(
        client_id,
        redirectUri,
        scopes,
        state
      );
    } else {
      // For desktop, use popup flow
      linkedInLogin();
    }
  };

  return (
    <div
      role="button"
      onClick={handleClick}
    >
      {props.children}
    </div>
  );
}
